import { useState, useEffect } from "react";
import { Trash2, TrendingUp, ChevronDown, ChevronUp, Camera, Download } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { apiFetch } from "@/lib/api";
import type { MetricEntry } from "./MetricsSummaryCards";

interface ProgressPhoto {
  id: string;
  date: string;
  photo_type: "front" | "side" | "back";
  storage_path: string;
  url?: string;
}

const photoLabels: Record<string, string> = {
  front: "Frente",
  side: "Lado",
  back: "Costas",
};

interface Props {
  entries: MetricEntry[];
  onDelete: (id: string) => void;
}

const MetricsHistory = ({ entries, onDelete }: Props) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [expandedPhoto, setExpandedPhoto] = useState<ProgressPhoto | null>(null);

  const handleDownload = async (photo: ProgressPhoto) => {
    if (!photo.url) return;
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `progresso-${photo.date}-${photo.photo_type}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail
    }
  };

  useEffect(() => {
    const loadPhotos = async () => {
      setLoadingPhotos(true);
      try {
        const res = await apiFetch("api-photos", { action: "list" });
        setPhotos(res.data || []);
      } catch {
        // silently fail - photos just won't show
      }
      setLoadingPhotos(false);
    };
    loadPhotos();
  }, []);

  const getPhotosForEntry = (entry: MetricEntry) => {
    if (entry.photoIds && entry.photoIds.length > 0) {
      return photos.filter((p) => entry.photoIds!.includes(p.id));
    }
    return [];
  };

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
          <TrendingUp className="w-7 h-7 text-muted-foreground" />
        </div>
        <p className="text-muted-foreground">Nenhum registro ainda</p>
        <p className="text-sm text-muted-foreground/70 mt-1">Registre suas medidas para acompanhar a evolução</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="font-semibold text-foreground">Histórico</h3>
      {[...entries].reverse().map((entry) => {
        const isExpanded = expandedId === entry.id;
        const datePhotos = getPhotosForEntry(entry);

        return (
          <div key={entry.id} className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
            <button
              onClick={() => setExpandedId(isExpanded ? null : entry.id)}
              className="w-full p-4 flex items-center gap-4 text-left"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {new Date(entry.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                </p>
                <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{entry.weight} kg</span>
                  {entry.arm && <span>Braço: {entry.arm}cm</span>}
                  {entry.waist && <span>Cintura: {entry.waist}cm</span>}
                  {entry.abdomen && <span>Abdômen: {entry.abdomen}cm</span>}
                  {entry.hip && <span>Quadril: {entry.hip}cm</span>}
                  {entry.thigh && <span>Coxa: {entry.thigh}cm</span>}
                  {entry.calf && <span>Panturrilha: {entry.calf}cm</span>}
                </div>
                {!isExpanded && datePhotos.length > 0 && (
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-primary">
                    <Camera className="w-3 h-3" />
                    <span>{datePhotos.length} foto{datePhotos.length > 1 ? "s" : ""}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            </button>

            {isExpanded && (
              <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                {/* Detailed measurements */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-muted/50 rounded-lg p-2.5">
                    <span className="text-muted-foreground">Peso</span>
                    <p className="font-semibold text-foreground">{entry.weight} kg</p>
                  </div>
                  {entry.arm && (
                    <div className="bg-muted/50 rounded-lg p-2.5">
                      <span className="text-muted-foreground">Braço</span>
                      <p className="font-semibold text-foreground">{entry.arm} cm</p>
                    </div>
                  )}
                  {entry.waist && (
                    <div className="bg-muted/50 rounded-lg p-2.5">
                      <span className="text-muted-foreground">Cintura</span>
                      <p className="font-semibold text-foreground">{entry.waist} cm</p>
                    </div>
                  )}
                  {entry.abdomen && (
                    <div className="bg-muted/50 rounded-lg p-2.5">
                      <span className="text-muted-foreground">Abdômen</span>
                      <p className="font-semibold text-foreground">{entry.abdomen} cm</p>
                    </div>
                  )}
                  {entry.hip && (
                    <div className="bg-muted/50 rounded-lg p-2.5">
                      <span className="text-muted-foreground">Quadril</span>
                      <p className="font-semibold text-foreground">{entry.hip} cm</p>
                    </div>
                  )}
                  {entry.thigh && (
                    <div className="bg-muted/50 rounded-lg p-2.5">
                      <span className="text-muted-foreground">Coxa</span>
                      <p className="font-semibold text-foreground">{entry.thigh} cm</p>
                    </div>
                  )}
                  {entry.calf && (
                    <div className="bg-muted/50 rounded-lg p-2.5">
                      <span className="text-muted-foreground">Panturrilha</span>
                      <p className="font-semibold text-foreground">{entry.calf} cm</p>
                    </div>
                  )}
                </div>

                {entry.note && (
                  <p className="text-xs text-muted-foreground italic bg-muted/30 rounded-lg p-2.5">
                    💬 {entry.note}
                  </p>
                )}

                {/* Progress photos */}
                {datePhotos.length > 0 ? (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                      <Camera className="w-3.5 h-3.5 text-primary" />
                      Fotos de Progresso
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {datePhotos.map((photo) => (
                        <div key={photo.id} className="relative cursor-pointer" onClick={() => setExpandedPhoto(photo)}>
                          <img
                            src={photo.url}
                            alt={photoLabels[photo.photo_type]}
                            className="w-full aspect-[3/4] object-cover rounded-lg transition-opacity hover:opacity-90"
                          />
                          <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">
                            {photoLabels[photo.photo_type]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground/60 italic">Nenhuma foto registrada nesta data</p>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      setDeletingId(entry.id);
                      await onDelete(entry.id);
                      setDeletingId(null);
                    }}
                    disabled={deletingId === entry.id}
                    className="text-xs text-destructive hover:text-destructive/80 flex items-center gap-1 transition-colors disabled:opacity-50"
                  >
                    {deletingId === entry.id ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-destructive/40 border-t-destructive rounded-full animate-spin" />
                        Excluindo...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-3.5 h-3.5" />
                        Excluir registro
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <Dialog open={!!expandedPhoto} onOpenChange={(open) => !open && setExpandedPhoto(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg p-0 bg-black/95 border-none overflow-hidden">
          {expandedPhoto && (
            <div className="relative flex flex-col items-center">
              <div className="absolute top-3 right-3 z-10 flex gap-2">
                <button
                  onClick={() => handleDownload(expandedPhoto)}
                  className="bg-white/20 hover:bg-white/30 text-white rounded-full p-2 transition-colors backdrop-blur-sm"
                  title="Baixar foto"
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
              <img
                src={expandedPhoto.url}
                alt={photoLabels[expandedPhoto.photo_type]}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <p className="text-white text-sm font-medium">
                  {photoLabels[expandedPhoto.photo_type]} — {new Date(expandedPhoto.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MetricsHistory;
